( () => {
  const HEADER_SELECTOR = 'header';
  const OFFSET_EXTRA = 25;

  /**
   * Resolve a hash like `#section-1` to a live element on the current page.
   * Returns null when the hash is empty, malformed, or no matching id exists,
   * so the click can fall through to default browser navigation.
   */
  const resolveTarget = ( hash ) => {
    if ( ! hash || hash === '#' ) {
      return null;
    }

    // `decodeURIComponent` throws a URIError on malformed percent-encoding
    // (e.g. "#%E0%A4%A"). That's an expected, non-fatal edge case — we
    // intentionally swallow it and fall back to the raw slice so the link
    // still resolves if an element with that literal id happens to exist.
    let id;
    try {
      id = decodeURIComponent( hash.slice( 1 ) );
    } catch {
      id = hash.slice( 1 );
    }

    if ( ! id ) {
      return null;
    }

    return document.getElementById( id );
  };

  const getHeaderOffset = () => {
    const header = document.querySelector( HEADER_SELECTOR );
    const headerHeight = header ? header.offsetHeight : 0;
    return headerHeight + OFFSET_EXTRA;
  };

  const scrollToTarget = ( target ) => {
    const top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
    window.scrollTo( {
      top,
      behavior: 'smooth',
    } );
  };

  /**
   * Treat a link as same-page when it has no href beyond a hash, or when its
   * pathname + search match the current location. Cross-page hashes keep the
   * default behavior so the browser can navigate and jump on load.
   */
  const isSamePageLink = ( anchor ) => {
    const rawHref = anchor.getAttribute( 'href' ) || '';
    if ( rawHref.startsWith( '#' ) ) {
      return true;
    }
    return anchor.pathname === window.location.pathname &&
      anchor.search === window.location.search &&
      anchor.host === window.location.host;
  };

  /**
   * Selectors owned by other scroll controllers (e.g. the TOC block plugin).
   * We bail out so this generic handler does not double-scroll or fight their
   * active-state logic.
   */
  const SKIP_WITHIN_SELECTOR = '.toc-block-wrapper';

  const handleClick = ( e ) => {
    if ( e.defaultPrevented ) {
      return;
    }
    const anchor = e.target.closest( 'a[href*="#"]' );
    if ( ! anchor ) {
      return;
    }
    if ( anchor.closest( SKIP_WITHIN_SELECTOR ) ) {
      return;
    }
    if ( anchor.target && anchor.target !== '_self' ) {
      return;
    }
    if ( e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ) {
      return;
    }
    if ( ! isSamePageLink( anchor ) ) {
      return;
    }

    const hash = anchor.hash;
    const target = resolveTarget( hash );
    if ( ! target ) {
      return;
    }

    e.preventDefault();
    scrollToTarget( target );

    if ( window.history?.pushState ) {
      window.history.pushState( null, '', hash );
    }
  };

  /**
   * If a TOC block links to this id, the TOC plugin owns the load-time scroll
   * (see toc-frontend.js `correctInitialHashPosition`). Yield to avoid a
   * double smooth-scroll with a slightly different offset.
   */
  const isClaimedByToc = ( target ) => {
    if ( ! target?.id ) {
      return false;
    }
    const escaped = window.CSS?.escape ? CSS.escape( target.id ) : target.id;
    return !! document.querySelector(
      `${ SKIP_WITHIN_SELECTOR } a[href="#${ escaped }"]`
    );
  };

  const handleInitialHash = () => {
    const target = resolveTarget( window.location.hash );
    if ( ! target ) {
      return;
    }
    if ( isClaimedByToc( target ) ) {
      return;
    }
    requestAnimationFrame( () => scrollToTarget( target ) );
  };

  document.addEventListener( 'DOMContentLoaded', () => {
    document.addEventListener( 'click', handleClick );
    window.addEventListener( 'load', handleInitialHash );
  } );
} )();
